/**
 * Staging flow DTO + completeness predicate (PF v2 native module, Wave 3).
 *
 * The module's durable state IS this DTO, stored at
 * `productConfigs["${cartItemId}|staging"]` (MODULE-CONTRACT Pattern A). It is the
 * per-room DRAFT working state (room set + per-room selections) — NOT the
 * submit-time image-keyed `StagingRequestDTO` (built at place-order, Wave 4).
 *
 * The shape is BYTE-IDENTICAL to the legacy adapter's `StagingDraftDTO`
 * (`adapters/staging/StagingAdapter.component.tsx:117-141`): the adapter already
 * flattened Immutable→plain arrays at the persisted boundary, so a draft saved
 * under the old adapter round-trips byte-identical under this module with zero
 * migration (§6 DONE gate). `PF_VERSION` stays FROZEN at 3. Do NOT rename fields.
 *
 * Plain-JS throughout (OQ-S5 ratified): `roomTypes`/`decorations` are string
 * arrays; `styleCodeMap`/`renovations` are `[key, value][]` pair arrays (never
 * Immutable). The module works in plain JS end-to-end — the legacy
 * `encodeRoom`/`decodeRoom` Immutable dance is gone.
 *
 * ZERO React / store imports — `modules/completeness.ts` (which
 * `store/selectors.ts` imports) pulls `stagingIsComplete` from here, so this file
 * must stay pure.
 */

import type {
  VirtualFurniturePropertyType,
  VirtualFurnitureRoomRemovableOption,
  VirtualFurnitureRoomType,
  VirtualFurnitureStyleCategory
} from 'constants/virtualFurniture'
import type { Nullable } from 'models/helpers'
import { StagingType } from 'models/virtualStaging'

import { isRoomCompleted } from './stagingRoom.logic'

/**
 * JSON-shaped snapshot of a single staging room. Byte-identical to the legacy
 * adapter `StagingDraftRoomDTO`. Immutable collections are represented as plain
 * arrays / `[key, value][]` pairs (the shape the adapter already persisted).
 */
export interface StagingDraftRoomDTO {
  productId: number
  key: string
  name: string
  roomTypes: VirtualFurnitureRoomType[]
  /** styleCodeMap entries: [roomType, styleCode][] */
  styleCodeMap: Array<[VirtualFurnitureRoomType, string]>
  styleCategory: Nullable<VirtualFurnitureStyleCategory>
  propertyType: Nullable<VirtualFurniturePropertyType>
  decorations: string[]
  roomCreationSortingOrder: number
  isComplete: boolean
  hasSelectedAllStyleCodes: boolean
  /** renovations entries: [roomType, renovationCode][] */
  renovations: Array<[VirtualFurnitureRoomType, string]>
  skipRenovations: boolean
  furnitureRemoval: Nullable<VirtualFurnitureRoomRemovableOption>
  comment: Nullable<string>
  type: StagingType
}

/** JSON-shaped snapshot of the staging draft. Keyed by roomKey (=== gcFilename). */
export interface StagingDraftDTO {
  rooms: Record<string, StagingDraftRoomDTO>
}

/**
 * Read-time fallback for a per-room field-merge (`{ ...ROOM_DEFAULT, ...raw }`).
 * Mirrors the legacy `createRoomObject` defaults (`RoomInventory.context.tsx:151-169`),
 * with the nullable fields normalized to `null` (as `decodeRoom` did). NEVER
 * written to the store on mount (read-only-mount invariant, MODULE-CONTRACT §3).
 */
export const ROOM_DEFAULT: Omit<StagingDraftRoomDTO, 'productId' | 'key' | 'name' | 'roomCreationSortingOrder'> = {
  roomTypes: [],
  styleCodeMap: [],
  styleCategory: null,
  propertyType: null,
  decorations: [],
  isComplete: false,
  hasSelectedAllStyleCodes: false,
  renovations: [],
  skipRenovations: false,
  furnitureRemoval: null,
  comment: null,
  type: StagingType.CUSTOM,
}

/**
 * Read-boundary per-room normalizer (§2 partial-DTO invariant). A persisted room
 * can be legitimately partial (autosave persists whatever was authored;
 * legacy/partial drafts). Merging over `ROOM_DEFAULT` guarantees every array field
 * is defined, so the pure logic (which reads `room.renovations.length` etc.) never
 * throws on a sparse room. Absent `productId` → `0` → `isRoomCompleted` returns
 * false (safe: a room with no product is incomplete).
 */
export const normalizeRoom = (roomKey: string, raw: Partial<StagingDraftRoomDTO> | undefined): StagingDraftRoomDTO => ({
  productId: 0,
  key: roomKey,
  name: '',
  roomCreationSortingOrder: 0,
  ...ROOM_DEFAULT,
  ...(raw ?? {}),
})

/**
 * Pure completeness predicate. RECOMPUTES per room via the pure `isRoomCompleted`
 * rather than trusting the stored `isComplete` snapshot (belt-and-suspenders; the
 * stored flag becomes advisory). Field-merges each room (partial-DTO invariant)
 * before recompute.
 *
 * ⚠️ W-21 (2026-08-11) — ZERO ROOMS IS COMPLETE, deliberately. The legacy adapter
 * predicate (`StagingAdapter.component.tsx:299-306`) required "rooms non-empty &&
 * every room complete", which hard-blocked Save + Proceed for a staging line with
 * no uploads. The BE confirmed in writing (Tom Fic, 2026-08-11) that
 * `inputImagePolicy: EXACTLY_COUNT` is a **BOUND, not a requirement** — "an empty
 * cart line is valid (staging add-on with no sources yet), server only checks
 * count when images are present". So an empty room set (and an absent DTO — the
 * shape when nothing was ever uploaded, since the E2 reconcile writes nothing for
 * an empty feed) is SAVEABLE and COMPLETE. The count/authoring validation is
 * untouched for rooms that DO exist: every present room must still pass
 * `isRoomCompleted`.
 *
 * Note this does not skip user confirmation — Proceed still gates on
 * `cartItemUserConfirmed` (`selectConfigurationStepValid`), i.e. the user must
 * still open the tab once.
 *
 * Reads the persisted DTO ONLY — never the live feed, never working state → stays
 * a pure `FLOW_IS_COMPLETE['staging']` predicate.
 */
export const stagingIsComplete = (dto: StagingDraftDTO | undefined): boolean => {
  const entries = dto?.rooms ? Object.entries(dto.rooms) : []
  if (entries.length === 0) return true // W-21 — zero source images is a valid staging line
  return entries.every(([roomKey, room]) => isRoomCompleted(normalizeRoom(roomKey, room)))
}
